import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function WaveSpeedCalculator() {
  const [frequency, setFrequency] = useState("");
  const [wavelength, setWavelength] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateWaveSpeed = () => {
    const f = parseFloat(frequency);
    const lambda = parseFloat(wavelength);

    if (isNaN(f) || isNaN(lambda)) {
      toast.error("Please enter valid values");
      return;
    }

    const speed = f * lambda;
    setResult(speed);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setFrequency("");
    setWavelength("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Wave Speed Calculator" 
      calculatorPath="/calculator/wave-speed"
      category="physics"
      subcategory="Waves"
    >
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: v = fλ</CardTitle>
            <CardDescription>
              Where v is wave speed (m/s), f is frequency (Hz), and λ is wavelength (m)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="frequency">Frequency (Hz)</Label>
                <Input
                  id="frequency"
                  type="number"
                  step="0.01"
                  placeholder="Enter frequency"
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="wavelength">Wavelength (m)</Label>
                <Input
                  id="wavelength"
                  type="number"
                  step="0.01"
                  placeholder="Enter wavelength"
                  value={wavelength}
                  onChange={(e) => setWavelength(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateWaveSpeed} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(2)} m/s
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Wave Speed
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["wave-speed"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter the frequency in Hertz</p>
                <p>2. Enter the wavelength in meters</p>
                <p>3. Click Calculate to get wave speed</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 100 Hz with 2 m wavelength = 200 m/s wave speed
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
